import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowUpRight, ChevronDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = ["AI Governance", "PMO Transformation", "Cybersecurity", "Enterprise Risk", "Multiple areas"];

const inputClass =
  "w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 text-sm text-white placeholder:text-slate-500 focus-visible:border-accent focus-visible:outline-none transition-colors sm:text-base";
const labelClass = "font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500";

export const BookingForm = ({ slot, date, onConfirmed }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", topic: TOPICS[0], notes: "" });
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, slot_start: slot.start, website: honeypot });
      onConfirmed({ ...data, name: form.name, email: form.email, topic: form.topic });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't complete the booking — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="booking-form" className="space-y-8">
      <p className="text-sm text-slate-300" data-testid="booking-form-summary">
        Booking <span className="text-gold">{format(date, "EEEE d MMMM")}</span> at{" "}
        <span className="text-gold">{slot.label}</span> UK time.
      </p>
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label htmlFor="booking-name" className={labelClass}>Full name *</label>
          <input id="booking-name" data-testid="booking-name-input" required maxLength={120} value={form.name} onChange={set("name")} placeholder="Alex Morgan" className={inputClass} />
        </div>
        <div>
          <label htmlFor="booking-email" className={labelClass}>Work email *</label>
          <input id="booking-email" data-testid="booking-email-input" type="email" required maxLength={160} value={form.email} onChange={set("email")} placeholder="alex@company.com" className={inputClass} />
        </div>
        <div>
          <label htmlFor="booking-company" className={labelClass}>Company</label>
          <input id="booking-company" data-testid="booking-company-input" maxLength={160} value={form.company} onChange={set("company")} placeholder="Company Ltd." className={inputClass} />
        </div>
        <div className="relative">
          <label htmlFor="booking-topic" className={labelClass}>Topic</label>
          <select id="booking-topic" data-testid="booking-topic-select" value={form.topic} onChange={set("topic")} className={`${inputClass} appearance-none pr-8`}>
            {TOPICS.map((t) => (
              <option key={t} value={t} className="bg-surface text-white">{t}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute bottom-4 right-1 h-4 w-4 text-slate-500" aria-hidden="true" />
        </div>
      </div>
      <div>
        <label htmlFor="booking-notes" className={labelClass}>Anything we should prepare for?</label>
        <textarea id="booking-notes" data-testid="booking-notes-input" maxLength={2000} rows={2} value={form.notes} onChange={set("notes")} placeholder="Optional — a line or two on what you'd like to cover" className={`${inputClass} resize-none`} />
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} className="hidden" />
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <button type="submit" data-testid="booking-submit-button" disabled={sending} className="btn-sweep btn-sweep-light flex items-center gap-3 border border-accent bg-accent px-10 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-ink">
          {sending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Confirming</>) : (<>Confirm booking <ArrowUpRight className="h-4 w-4" /></>)}
        </button>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Instant confirmation by email</span>
      </div>
    </form>
  );
};
